import { CurrencyMap, CustomerServiceProvider, Country } from '@/common/config';
import Api from '@/common/api';
import * as PleasedApi from '@/common/api/api.pleased';
import * as Locale from '@/plugins/i18n';
import * as env from '@/common/environment';
import { platform } from '@/common/bridge';
import { InitializeOptions, LoginResult, PleasedUserInfo } from '@/common/types';
import { ActionContext } from 'vuex';
import { reportingService } from '@/common/reporting';

export interface UserState {
  isLogin: boolean;
  country: Country | string;
  token?: string;
  chatStatus?: string; // null, chat-bot-start,  live-chat
  avatarImgUrl: string;
  phone: string;
  isEmailVerified: boolean;
  provider: CustomerServiceProvider | '',
  directLink: boolean;
  customerAppVersion: string | null;
  supportVoice: boolean;
  pleasedUser: PleasedUserInfo;
  hideLoginButton: boolean;
  /**
   * Email provided from init.
   */
  email: string;
  metaTags: Record<string, string | number | boolean>;
  /**
   * Transaction currency.
   */
  userCurrency: string;
  usernameFieldType: 'phone' | 'email';

  sportyLoginPromise: Promise<unknown> | null;
}

interface InitOption {
  token: string,
  country: Country, avatarImgUrl: string, chatStatus: string, phone: string, provider: CustomerServiceProvider, directLink: boolean | null, appVersion: string | null, supportVoice: boolean | null
}

const state: UserState = {
  isLogin: false,
  country: '',
  token: '',
  chatStatus: '',
  avatarImgUrl: '',
  phone: '',
  isEmailVerified: false,
  provider: CustomerServiceProvider.PLEASED,
  directLink: false,
  customerAppVersion: null,
  supportVoice: true,
  pleasedUser: {
    userId: '',
    foreignId: '',
    email: '',
    phone: '',
    token: '',
    displayName: '',
    isGuest: true,
  },
  email: '',
  metaTags: {},
  userCurrency: '',
  usernameFieldType: 'phone',
  sportyLoginPromise: null,
  hideLoginButton: false,
};

const getters = {
  maskPhone (state: UserState) { // user phone 777778888711 -> 77*******711
    if (!state.phone) return '***';
    const length = state.phone.length; // TODO:BUG
    const noMask = [0, 1, length - 1, length - 2, length - 3];
    return state.phone && state.phone.split('').reduce((acc, cur, i) => {
      return noMask.includes(i) ? `${acc}${cur}` : `${acc}*`;
    }, '');
  },
  currency (state: UserState) {
    return state.country ? state.userCurrency || CurrencyMap[state.country] : '';
  },
  maskEmail (state: UserState) {
    if (!state.email) return '***';
    return state.email.replace(/^(.)(.*)(.@.*)$/,
      (_, a, b, c) => a + b.replace(/./g, '*') + c
    );
  },
  maskName (state: UserState, getters: Record<string, unknown>) {
    if (state.phone) return getters.maskPhone;
    if (state.email) return getters.maskEmail;
    return '***';
  },
  displayName (state: UserState) {
    return state.phone || state.email;
  },
};

const mutations = {
  deleteChatStatus (state: UserState) {
    state.chatStatus = '';
  },
  // For parents Event
  // INIT
  initProject (state: UserState, payload: InitializeOptions) {
    const country = payload.sporty?.country || payload.country;
    Api.setCountry(country as Country);
    state.provider = payload.provider;
    Api.setProvider(payload.provider);

    state.hideLoginButton = !!payload.hideLoginButton;

    state.country = country;
    // state.chatStatus = payload.chatStatus; // currently, Pleased cannot keep the conversation sessions
    state.directLink = !!payload.directLink;
    if (payload.appVersion) {
      state.customerAppVersion = payload.appVersion;
    }
    // android should do the implementation below:
    // 1. AndroidManifest.xml for microphone permission
    // 2, android.webkit.resource.AUDIO_CAPTURE // webrtc request permission
    // 3. webSettings.setMediaPlaybackRequiresUserGesture(false); // without this, apk can only send sound but cannot hear the sound
    if (platform === 'app') {
      state.supportVoice = !!payload.supportVoice;
    } else if (platform === 'ios') { // ios webview doesn't support webrtc
      state.supportVoice = false;
    }

    // Resolve the configuration by country.
    env.setup(country, payload);
    PleasedApi.setConfig({
      // Prefer client configuration.
      url: payload.pleasedUrl || env.get('url'),
      brandCode: payload.brandCode || env.get('brandCode'),
    });

    const token = payload.sporty?.token || payload.token;
    state.userCurrency = payload.sporty?.currency || '';

    if (token) {
      Api.setTokenToHeader(token); // Call before set token in case race condition
      state.token = token;
      state.isLogin = true;
      state.phone = payload.phone || '';
      state.email = payload.email || '';
    }

    state.metaTags = { ...state.metaTags, ...payload.metaTags };

    state.usernameFieldType = payload.login?.usernameType || env.get('userFieldType')?.[country as Country] || 'phone';

    if (payload.defaultLanguage && Locale.isSupported(payload.defaultLanguage) && env.isLocaleEnabled(payload.defaultLanguage)) {
      Locale.setLanguage(payload.defaultLanguage);
    }
  },
  updatePleasedUser (state: UserState, payload: PleasedUserInfo) {
    state.pleasedUser = payload;
    PleasedApi.setConfig({ token: payload.token });
  },
  // LOGIN, LOGIN_POP
  updateLoginStatus (state: UserState, payload: LoginResult) {
    if (payload.token) {
      state.token = payload.token;
      state.isLogin = true;
      state.phone = payload.phone || '';
      state.email = payload.email || '';
      Api.setTokenToHeader(payload.token);
    }
    state.metaTags = { ...state.metaTags, ...payload.metaTags };
    if (state.avatarImgUrl) {
      state.avatarImgUrl = payload.avatarImgUrl || '';
    }
  },
  updateAvatarImgUrl (state: UserState, url: string) {
    if (url) { state.avatarImgUrl = url; }
  },
  updateEmailVeryfied (state: UserState, bool: boolean) {
    state.isEmailVerified = bool;
  },
  setSportyPromise (state: UserState, promise: Promise<unknown> | null) {
    state.sportyLoginPromise = promise;
  },

  setUserPhoneOrEmailIfNotExists (state: UserState, payload: Partial<Pick<UserState, 'phone' | 'email'>>) {
    if (payload.phone && !(state.phone || '').trim()) state.phone = `${payload.phone}`.trim();
    if (payload.email && !(state.email || '').trim()) state.email = `${payload.email}`.trim();
  },
};

const actions = {
  async sportyLoginSuccess ({ state, commit }: ActionContext<UserState, unknown>, payload: LoginResult) {
    if (state.sportyLoginPromise) {
      await state.sportyLoginPromise;
    }
    if (payload.token !== state.token || !state?.pleasedUser?.token) {
      commit('updateLoginStatus', payload);
      const promise = PleasedApi.getPleasedTokenFromSportyToken({ countryCode: state.country, sportyToken: payload.token });
      commit('setSportyPromise', promise.then(() => { commit('setSportyPromise', null); }));
      const res = await promise;
      reportingService.userLoggedIn();
      commit('updatePleasedUser', {
        userId: res.id,
        foreignId: res.foreignId,
        email: res.email || '',
        phone: res.phone || payload.phone || '',
        token: res.token,
        displayName: res.name || res.phone || res.defaultName || '',
        isGuest: false,
      });
    } else { console.warn('[User:sportyLoginSuccess]: Token already registered'); }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
